import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calculator as CalcIcon, TrendingDown } from 'lucide-react';
import { pricingPlans } from '../mock';

const Calculator = () => {
  const [monthlyPrice, setMonthlyPrice] = useState(1200);
  const [selectedPlan, setSelectedPlan] = useState(2);
  
  // Find selected plan details
  const currentPlan = pricingPlans.find(plan => plan.years === selectedPlan) || pricingPlans[0];
  const discountRate = currentPlan.discount / 100;
  
  const discountedPrice = Math.round(monthlyPrice * (1 - discountRate));
  const totalMonths = selectedPlan * 12;
  const totalNormal = monthlyPrice * totalMonths;
  const totalPrepaid = discountedPrice * totalMonths;
  const totalSavings = totalNormal - totalPrepaid;

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-24 bg-gradient-to-br from-green-50 to-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-2xl mb-6">
            <CalcIcon className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Calculadora de ahorro
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Elige tu plan y descubre cuánto puedes ahorrar con nuestro modelo de prepago
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Calculator Input */}
            <div className="space-y-6">
              <div>
                <label htmlFor="monthly-price" className="block text-sm font-semibold text-gray-700 mb-3">
                  Precio mensual del alquiler (€)
                </label>
                <div className="relative">
                  <Input
                    id="monthly-price"
                    type="number"
                    value={monthlyPrice}
                    onChange={(e) => setMonthlyPrice(Number(e.target.value) || 0)}
                    className="text-2xl font-bold h-14 pl-6 pr-12 border-2 border-gray-200 focus:border-green-500 rounded-xl"
                    min="100"
                    max="10000"
                    step="50"
                  />
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 font-bold">
                    €/mes
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="plan-selector" className="block text-sm font-semibold text-gray-700 mb-3">
                  Selecciona tu plan
                </label>
                <Select value={selectedPlan.toString()} onValueChange={(value) => setSelectedPlan(Number(value))}>
                  <SelectTrigger className="h-14 border-2 border-gray-200 focus:border-green-500 rounded-xl text-lg">
                    <SelectValue placeholder="Elige tu plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {pricingPlans.map((plan) => (
                      <SelectItem key={plan.years} value={plan.years.toString()} className="text-lg py-3">
                        <div className="flex flex-col">
                          <span className="font-semibold">{plan.years} años - {plan.discount}% descuento</span>
                          <span className="text-sm text-gray-500">{plan.title}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-green-50 rounded-2xl p-6 border-2 border-green-200">
                <div className="flex items-center gap-3 mb-4">
                  <TrendingDown className="w-6 h-6 text-green-600" />
                  <span className="font-bold text-green-800">Tu precio con descuento</span>
                </div>
                <div className="text-3xl font-bold text-green-600">
                  {discountedPrice.toLocaleString()}€ <span className="text-lg text-green-500">/mes</span>
                </div>
                <p className="text-green-700 text-sm mt-2">
                  {currentPlan.discount}% de descuento aplicado - {currentPlan.title}
                </p>
              </div>
            </div>

            {/* Results */}
            <div className="space-y-6">
              <Card className="border-2 border-gray-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg text-gray-700">Resumen de {selectedPlan} años</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Alquiler tradicional:</span>
                    <span className="font-bold text-gray-900">{totalNormal.toLocaleString()}€</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Con prepago:</span>
                    <span className="font-bold text-green-600">{totalPrepaid.toLocaleString()}€</span>
                  </div>
                  <hr className="border-gray-200" />
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-lg text-gray-900">Tu ahorro total:</span>
                    <span className="font-bold text-2xl text-green-600">{totalSavings.toLocaleString()}€</span>
                  </div>
                </CardContent>
              </Card>

              <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-6 text-white">
                <h3 className="font-bold text-lg mb-2">¡Ahorro garantizado!</h3>
                <p className="text-green-100 mb-4">
                  Con {monthlyPrice}€/mes y el plan de {selectedPlan} años, ahorras {totalSavings.toLocaleString()}€ 
                  ({currentPlan.discount}% de descuento)
                </p>
                <Button 
                  onClick={scrollToContact}
                  className="w-full bg-white text-green-600 hover:bg-gray-100 font-bold py-3"
                >
                  Quiero este ahorro
                </Button>
              </div>
            </div>
          </div>

          {/* Plans Summary */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
              Nuestros planes disponibles
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              {pricingPlans.map((plan) => (
                <div
                  key={plan.years}
                  className={`p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                    selectedPlan === plan.years 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                  onClick={() => setSelectedPlan(plan.years)}
                >
                  <div className="text-center">
                    <div className="font-bold text-lg text-gray-900">{plan.title}</div>
                    <div className="text-2xl font-bold text-green-600 my-2">
                      {plan.discount}% descuento
                    </div>
                    <div className="text-gray-600 text-sm">{plan.years} años</div>
                    <div className="text-gray-500 text-xs mt-1">{plan.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Calculator;